/* eslint-disable */
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Box, Heading, Text, SimpleGrid, Flex, Button, Icon, useColorModeValue,
} from "@chakra-ui/react";
import { FaVideo, FaCalendarAlt, FaClock, FaUser, FaLock } from "react-icons/fa";
import { useModalToast } from "../../context/GlobalModalToast";

/* ─── Countdown hook ─── */
function useSessionCountdown(scheduledAt) {
  const getState = useCallback(() => {
    const now = Date.now(), start = new Date(scheduledAt).getTime();
    const unlockAt = start - 60000;
    const end = new Date(scheduledAt); end.setHours(23,59,59,999);
    return { isUnlocked: now>=unlockAt && now<=end.getTime(), isExpired: now>end.getTime(), diff: Math.max(0,unlockAt-now) };
  }, [scheduledAt]);
  const [state, setState] = useState(getState);
  useEffect(() => {
    if(state.isUnlocked||state.isExpired) return;
    const t=setInterval(()=>{ const n=getState(); setState(n); if(n.isUnlocked||n.isExpired)clearInterval(t); },1000);
    return ()=>clearInterval(t);
  },[getState,state.isUnlocked,state.isExpired]);
  const fmt=(ms)=>{ const s=Math.floor(ms/1000),h=Math.floor(s/3600),m=Math.floor((s%3600)/60),ss=s%60; return h>0?`${h}h ${m}m ${ss}s`:m>0?`${m}m ${ss}s`:`${ss}s`; };
  return { isUnlocked:state.isUnlocked, isExpired:state.isExpired, countdown:fmt(state.diff) };
}

/* ─── Sector → hero palette color map ─── */
const sectorStyle = (sector) => {
  const map = {
    "Global":     { tag:"soft-tag-blue",   topGrad:"linear-gradient(135deg,#4285F4,#06B6D4)", glow:"0 0 0 3px rgba(66,133,244,0.16),0 8px 24px rgba(66,133,244,0.22)",  accentRGB:"66,133,244" },
    "Technology": { tag:"soft-tag-green",  topGrad:"linear-gradient(135deg,#34A853,#06B6D4)", glow:"0 0 0 3px rgba(52,168,83,0.16),0 8px 24px rgba(52,168,83,0.22)",   accentRGB:"52,168,83"  },
    "HR":         { tag:"soft-tag-red",    topGrad:"linear-gradient(135deg,#EA4335,#C58AF9)", glow:"0 0 0 3px rgba(234,67,53,0.16),0 8px 24px rgba(234,67,53,0.22)",    accentRGB:"234,67,53"  },
    "Finance":    { tag:"soft-tag-yellow", topGrad:"linear-gradient(135deg,#FBBC04,#F97316)", glow:"0 0 0 3px rgba(251,188,4,0.20),0 8px 24px rgba(251,188,4,0.26)",    accentRGB:"251,188,4"  },
    "Operations": { tag:"soft-tag-purple", topGrad:"linear-gradient(135deg,#C58AF9,#7C6FF7)", glow:"0 0 0 3px rgba(197,138,249,0.18),0 8px 24px rgba(197,138,249,0.24)",accentRGB:"197,138,249"},
    "Legal":      { tag:"soft-tag-cyan",   topGrad:"linear-gradient(135deg,#06B6D4,#4285F4)", glow:"0 0 0 3px rgba(6,182,212,0.16),0 8px 24px rgba(6,182,212,0.22)",    accentRGB:"6,182,212"  },
  };
  return map[sector] || map["Global"];
};

/* ─── Session Card ─── */
function SessionCard({ session }) {
  const { isUnlocked, isExpired, countdown } = useSessionCountdown(session.scheduledAt);
  const ss = sectorStyle(session.targetSector);

  const date = new Date(session.scheduledAt);
  const fDate = date.toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" });
  const fTime = date.toLocaleTimeString("en-US", { hour:"2-digit", minute:"2-digit" });

  return (
    <Box
      borderRadius="3xl" overflow="hidden" bgImage={ss.topGrad}
      boxShadow="0 20px 40px rgba(0,0,0,0.2)"
      opacity={isExpired ? 0.60 : 1}
      transition="all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)"
      _hover={{ transform:"translateY(-4px)", boxShadow: "0 25px 50px rgba(0,0,0,0.3)" }}
      p={6}
      position="relative"
    >
      {/* Background glow effect */}
      <Box 
        position="absolute" top="-50px" right="-50px" w="150px" h="150px" 
        bg="white" opacity="0.1" filter="blur(40px)" borderRadius="full" 
      />

      {/* Header */}
      <Flex justify="space-between" align="flex-start" mb={6}>
        <Flex align="center" gap={3}>
          <Box bg="rgba(255,255,255,0.1)" p={2} borderRadius="full">
            <Icon as={FaVideo} color="white" boxSize="16px" />
          </Box>
          <Box>
            <Heading size="xs" fontWeight="900" color="white" textTransform="uppercase" letterSpacing="1px" mb={1}>
              {session.targetSector} Session
            </Heading>
            <Text fontSize="10px" color="rgba(255,255,255,0.9)" fontWeight="800">
              {session.title}
            </Text>
          </Box>
        </Flex>
      </Flex>

      {/* Center Big Typography (Countdown or Time) */}
      <Box mb={6}>
        <Text fontSize="11px" color="rgba(255,255,255,0.8)" fontWeight="700" mb={1} letterSpacing="0.5px">
          {isUnlocked ? "SESSION IS LIVE" : isExpired ? "SESSION ENDED" : "STARTS IN"}
        </Text>
        <Heading fontSize="6xl" fontWeight="800" color="white" letterSpacing="-2px" lineHeight="1" mb={2}>
          {isUnlocked ? "LIVE" : isExpired ? "END" : countdown.split(' ')[0]}
        </Heading>
        <Text fontSize="13px" color="white" fontWeight="800" letterSpacing="0.5px">
          {fDate} at {fTime} · {session.duration}m
        </Text>
      </Box>

      {/* CTA Button */}
      {isUnlocked ? (
        <Button
          as="a"
          href={session.meetingLink.startsWith("http") ? session.meetingLink : `https://${session.meetingLink}`}
          target="_blank"
          w="full" h="50px"
          bg="white" color="gray.900"
          borderRadius="xl" fontWeight="800" fontSize="md"
          boxShadow="0 8px 20px rgba(0,0,0,0.15)"
          _hover={{ opacity:0.95, transform:"translateY(-2px)" }}
          transition="all 0.2s"
        >
          Join Live Session
        </Button>
      ) : (
        <Button
          w="full" h="50px"
          bg="rgba(0,0,0,0.15)" color={isExpired ? "rgba(255,255,255,0.6)" : "white"}
          borderRadius="xl" fontWeight="800" fontSize="sm"
          cursor={isExpired ? "not-allowed" : "default"}
          _hover={{ bg: "rgba(0,0,0,0.2)" }}
        >
          {isExpired ? "Ended" : `Host: ${session.hostName}`}
        </Button>
      )}
    </Box>
  );
}

/* ─── Widget ─── */
export default function LiveSessionsWidget() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(true);
  const textMain = useColorModeValue("var(--text-main)", "var(--text-main)");

  useEffect(() => {
    (async () => {
      try {
        const token = JSON.parse(localStorage.getItem("user"))?.token||"";
        if(!token) return;
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/livesessions`,
          { headers: { Authorization:`Bearer ${token}` } }
        );
        setSessions(res.data);
      } catch(err){ console.error(err); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading || sessions.length===0) return null;

  return (
    <Box mt={10}>
      <Flex align="center" gap={3} mb={6}>
        <Box className="icon-chip icon-chip-pink" w="38px" h="38px" borderRadius="12px">
          <FaVideo size={14}/>
        </Box>
        <Box>
          <Text className="section-label">Instructor-Led</Text>
          <Heading size="md" fontWeight="900" color={textMain} letterSpacing="-0.4px">
            Upcoming Live Sessions
          </Heading>
        </Box>
      </Flex>
      <SimpleGrid columns={{base:1,md:2,lg:3}} gap={5}>
        {sessions.map(s=><SessionCard key={s._id} session={s}/>)}
      </SimpleGrid>
    </Box>
  );
}
